import type { DealItemWithVersions } from '@/lib/graphql/generated/graphql';
import {
  isDealItemCategoryUpdating,
  resolveDealItemFromMap,
} from '@/lib/utils/updating-fields';
import { productsMapState } from '@/states/products.state';
import type { Row, Table } from '@tanstack/react-table';
import type { useStore } from 'jotai';

/**
 * Catégorisation en cours sur cette ligne ou l'une de ses feuilles (map = source optimiste).
 *
 * TanStack Table v8 `getLeafRows()` est implémenté comme `flattenBy(row.subRows, …)` uniquement :
 * la ligne courante n’y figure pas. Pour une feuille sans sous-lignes, `getLeafRows()` renvoie
 * `[]` — il faut donc tester `row.original` (ne pas remplacer par `getLeafRows().some` seul).
 */
export function isTableRowAnyCategoryUpdatingFromMap(
  row: Row<DealItemWithVersions>,
  productsMap: Map<string, DealItemWithVersions>
): boolean {
  const leaves = row.getLeafRows();
  if (leaves.length === 0) {
    return isDealItemCategoryUpdating(
      resolveDealItemFromMap(productsMap, row.original)
    );
  }
  return leaves.some((lr) =>
    isDealItemCategoryUpdating(resolveDealItemFromMap(productsMap, lr.original))
  );
}

export function isCategorySelectionBlocked(
  store: ReturnType<typeof useStore>,
  item: DealItemWithVersions,
  productsMap?: Map<string, DealItemWithVersions>
): boolean {
  const map = productsMap ?? store.get(productsMapState);
  return isDealItemCategoryUpdating(resolveDealItemFromMap(map, item));
}

export function isTableRowCheckboxDisabled(
  store: ReturnType<typeof useStore>,
  row: Row<DealItemWithVersions>,
  isOfferLocked: boolean,
  productsMap?: Map<string, DealItemWithVersions>
): boolean {
  if (isOfferLocked) return true;
  const map = productsMap ?? store.get(productsMapState);
  return isTableRowAnyCategoryUpdatingFromMap(row, map);
}

/** Toutes les lignes visibles ont une case à cocher désactivée (ex. tout en catégorisation). */
export function areAllTableRowCheckboxesDisabled(
  rows: Row<DealItemWithVersions>[],
  store: ReturnType<typeof useStore>,
  isOfferLocked: boolean,
  productsMap?: Map<string, DealItemWithVersions>
): boolean {
  if (isOfferLocked) return true;
  if (rows.length === 0) return true;
  const map = productsMap ?? store.get(productsMapState);
  return rows.every((row) =>
    isTableRowCheckboxDisabled(store, row, false, map)
  );
}

/** Jeux d’ids pour « tout sélectionner » : un passage sur les feuilles filtrées + map. */
export type LeafCheckboxSelectionSets = {
  selectableIds: string[];
  excludedWhenSelectAll: Set<string>;
};

/** Passe unique sur les lignes filtrées (feuilles) ; `productsMap` déjà résolue côté appelant. */
export function computeLeafCheckboxSelectionSetsFromFlatRows(
  flatRows: Row<DealItemWithVersions>[],
  productsMap: Map<string, DealItemWithVersions>,
  isOfferLocked: boolean
): LeafCheckboxSelectionSets {
  if (isOfferLocked) {
    return { selectableIds: [], excludedWhenSelectAll: new Set() };
  }
  const selectableIds: string[] = [];
  const excludedWhenSelectAll = new Set<string>();
  const seenSelectable = new Set<string>();

  for (const row of flatRows) {
    if (row.subRows.length > 1) continue;

    const item = row.original;
    const blocked = isDealItemCategoryUpdating(
      resolveDealItemFromMap(productsMap, item)
    );
    if (blocked) {
      excludedWhenSelectAll.add(item.id);
    } else if (!seenSelectable.has(item.id)) {
      seenSelectable.add(item.id);
      selectableIds.push(item.id);
    }
  }
  return { selectableIds, excludedWhenSelectAll };
}

export function computeLeafCheckboxSelectionSets(
  table: Table<DealItemWithVersions>,
  store: ReturnType<typeof useStore>,
  isOfferLocked: boolean
): LeafCheckboxSelectionSets {
  if (isOfferLocked) {
    return { selectableIds: [], excludedWhenSelectAll: new Set() };
  }
  const map = store.get(productsMapState);
  return computeLeafCheckboxSelectionSetsFromFlatRows(
    table.getFilteredRowModel().flatRows,
    map,
    isOfferLocked
  );
}

export function filterSelectableLeafIds(
  store: ReturnType<typeof useStore>,
  ids: string[]
): string[] {
  const map = store.get(productsMapState);
  return ids.filter((id) => {
    const latest = map.get(id);
    if (!latest) return true;
    return !isDealItemCategoryUpdating(latest);
  });
}
